const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/;
const US_DATE_REGEX = /^(\d{2})-(\d{2})-(\d{4})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/;

const pad = (value) => value.toString().padStart(2, '0');

const normalizeDateParts = (parts) => {
  if (!parts) return null;
  const { year, month, day, hour, minute, second } = parts;
  if (!year || !month || !day) return null;
  return {
    year,
    month,
    day,
    hour: hour ?? undefined,
    minute: minute ?? undefined,
    second: second ?? undefined,
  };
};

const parseWithRegex = (value, regex, order) => {
  const match = regex.exec(value);
  if (!match) return null;
  const [, a, b, c, hour, minute, second] = match;
  const mapping = {
    YMD: { year: Number(a), month: Number(b), day: Number(c) },
    MDY: { month: Number(a), day: Number(b), year: Number(c) },
  };
  const base = mapping[order];
  if (!base) return null;
  return normalizeDateParts({
    ...base,
    hour: hour !== undefined ? Number(hour) : undefined,
    minute: minute !== undefined ? Number(minute) : undefined,
    second: second !== undefined ? Number(second) : undefined,
  });
};

const parseDateParts = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
      hour: value.getHours(),
      minute: value.getMinutes(),
      second: value.getSeconds(),
    };
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const isoParts = parseWithRegex(raw, ISO_DATE_REGEX, 'YMD');
  if (isoParts) return isoParts;

  const usParts = parseWithRegex(raw, US_DATE_REGEX, 'MDY');
  if (usParts) return usParts;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;

  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1,
    day: parsed.getDate(),
    hour: parsed.getHours(),
    minute: parsed.getMinutes(),
    second: parsed.getSeconds(),
  };
};

const formatDateParts = (parts) => {
  if (!parts) return null;
  const normalized = normalizeDateParts(parts);
  if (!normalized) return null;
  const { year, month, day } = normalized;
  return `${pad(month)}-${pad(day)}-${year}`;
};

export const formatDisplayDate = (value, fallback = '') => {
  const formatted = formatDateParts(parseDateParts(value));
  if (!formatted) return fallback;
  return formatted;
};

export const formatDisplayDateTime = (value, fallback = '') => {
  const parts = parseDateParts(value);
  const normalized = normalizeDateParts(parts);
  if (!normalized) return fallback;
  const datePortion = formatDateParts(normalized);
  if (!datePortion) return fallback;

  const { hour, minute, second } = normalized;
  if (hour === undefined || minute === undefined) {
    return datePortion;
  }

  const secondsSegment = second !== undefined ? `:${pad(second)}` : '';
  const timePortion = `${pad(hour)}:${pad(minute)}${secondsSegment}`;
  return `${datePortion} ${timePortion}`;
};

export const formatDateForFilename = (value = new Date()) => {
  const parts = parseDateParts(value);
  const formatted = formatDateParts(parts);
  return formatted || '';
};

export const formatTimestampForFilename = (value = new Date()) => {
  const parts = parseDateParts(value);
  const normalized = normalizeDateParts(parts);
  if (!normalized) return '';
  const datePortion = formatDateParts(normalized);
  if (!datePortion) return '';
  const hour = pad(normalized.hour ?? 0);
  const minute = pad(normalized.minute ?? 0);
  const second = pad(normalized.second ?? 0);
  return `${datePortion}_${hour}-${minute}-${second}`;
};
